// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import { LazyImageStyle } from "@insite/mobius/LazyImage";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { bannerStyles } from "@synd/Overrides/Widgets/Basic/Banner";
import { layoutContainer, xScreen } from "@synd/XDL/Utilities";
import { css } from "styled-components";

bannerStyles.wrapper = {
    css: css`
        border-radius: ${({ isOverlay }: any) => (isOverlay ? "8px" : "0px")};
        overflow: hidden;
        position: relative;
        flex-wrap: nowrap;
        display: grid;
        grid-template-columns: ${({ isOverlay, imageWidth, imagePosition }: any) =>
            isOverlay
                ? "1fr"
                : imagePosition === "left"
                  ? css`
                        ${imageWidth || "minmax(0, 1fr)"} minmax(0, 1fr)
                    `
                  : css`
                      minmax(0, 1fr) ${imageWidth || "minmax(0, 1fr)"}`};

        ${xScreen("gte", "xl")} {
            ${layoutContainer};
            &:not(.full-width) {
                padding: 0;
            }
        }
        ${xScreen("lte", "md")} {
            display: flex;
            flex-direction: column;
        }
    `,
};

bannerStyles.imageWrapperStyles = {
    css: css`
        flex: 1;
        display: flex;
        grid-column: ${({ imagePosition, isOverlay }: any) => (!isOverlay && imagePosition === "right" ? "2" : "1")};
        ${({ isOverlay, imagePosition }: any) =>
            !isOverlay &&
            css`
                ${imagePosition === "right" &&
                css`
                    grid-column: 2;
                `}
                grid-row: 1;
            `}
        ${LazyImageStyle} {
            width: 100%;
            height: 100%;
            aspect-ratio: 785/442;
            ${({ isOverlay }: any) =>
                isOverlay
                    ? css`
                          max-height: 320px;
                          border-radius: 8px;
                      `
                    : css`
                          border-radius: 0 8px 8px 0;
                      `};

            ${xScreen("gte", "xl")} {
                border-radius: 8px;
            }
            ${xScreen("lte", "md")} {
                width: 100%;
                ${({ isOverlay }: any) =>
                    !isOverlay &&
                    css`
                        border-radius: 0;
                    `}
            }
            picture {
                display: flex;
                height: inherit;
                width: inherit;
            }
            img {
                flex: 1;
                object-fit: cover;
            }
        }
        video {
            border-radius: 0 8px 8px 0;
            ${xScreen("lte", "md")} {
                border-radius: 0;
            }
        }
    `,
};

bannerStyles.videoStyles = {
    container: {
        css: css`
            aspect-ratio: 1/1;
            max-height: 430px;
            flex: 1;
            margin: 0 auto;
            padding-bottom: 0;
            .yt-lite {
                height: 100%;
            }
        `,
    },
};

bannerStyles.textWrapperStyles = {
    css: css`
        display: flex;
        flex: 1;
        gap: 16px;
        background-color: ${({ backgroundColor }: any) => backgroundColor || "transparent"};
        padding: 22px;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        min-height: 100%;
        ${({ isOverlay }: any) =>
            !isOverlay &&
            css`
                grid-row: 1;
            `}
        ${({ isOverlay }: any) =>
            isOverlay &&
            css`
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                padding: 24px;
                min-height: unset;
                border-radius: 8px;
                align-items: center;
                ${TypographyStyle} {
                    text-align: center;
                }
            `}
        ${xScreen("lte", "md")} {
            width: 100%;
            padding: 16px;
            position: static;
            background-color: transparent;
            transform: unset;
        }
    `,
};

bannerStyles.heading = {
    css: css`
        ${TypographyStyle}:not(:empty) + ${TypographyStyle}:not(:empty) {
            margin-top: 8px;
        }
        ${TypographyStyle} {
            margin-bottom: 0px;
        }
    `,
};

bannerStyles.subHeading = {
    css: css`
        ${TypographyStyle} {
            line-height: ${getProp("theme.typography.h5.lineHeight")};
            font-size: ${getProp("theme.typography.h5.size")}px;
            margin-bottom: 0;
        }
    `,
};

bannerStyles.bannerButton = {
    css: css`
        width: fit-content;

        ${IconWrapper} {
            top: 3px;
            width: 10px;
            svg {
                width: 10px;
                height: 14px;
            }
        }
    `,
};
